import { useState, useEffect, useRef, useMemo } from "react";
import {Link, useNavigate, useLocation} from "react-router-dom";

import { Title } from "@/components";
import { setAuth } from "@redux/profile/authAndReg/slice";
import { useAppDispatch } from "@redux/store";
import { getTokenFromStorage } from "@/utils";

import { HeaderPropsType } from "./types";

import styles from "./header.module.scss";
import logo from "@assets/img/logo.svg";
import arrowLeftIcon from "@assets/icons/arrowCalendarLeft.svg";
import { mainMenuItems } from "./mainMenuItems";
import { MainMenuItem } from "./components/MainMenuItem/MainMenuItem";
import { HeaderRightButton } from "./components/HeaderRightButton/HeaderRightButton";

const menuItems = mainMenuItems;

export const Header: React.FC<HeaderPropsType> = (props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const mainMenuRef = useRef<HTMLDivElement>(null);
  const mainMenuButtonRef = useRef<HTMLButtonElement>(null);

  const { title, headerType = "page", returnButton = false, onBack } = props;

  const [ isMainMenuMobileOpen, setIsMainMenuMobileOpen ] = useState(false);
  const [ activeSubMenu, setActiveSubMenu ] = useState<string | null>(null);

  useEffect(() => {
    const isAuth = getTokenFromStorage();
    dispatch(setAuth(isAuth));

    document.body.addEventListener("click", clickOutside);

    return () => {
      document.body.removeEventListener("click", clickOutside);
      // Восстанавливаем скролл при размонтировании компонента
      document.body.style.overflow = "";
    };
  }, []);

  // Закрывать меню при изменении маршрута
  useEffect(() => {
    setIsMainMenuMobileOpen(false);
    setActiveSubMenu(null);
    document.body.style.overflow = "";
  }, [location.pathname]);

  const clickOutside = (e: MouseEvent) => {
    if (
      mainMenuRef.current &&
      !e.composedPath().includes(mainMenuRef.current) &&
      mainMenuButtonRef.current &&
      !e.composedPath().includes(mainMenuButtonRef.current)
    ) {
      setIsMainMenuMobileOpen(false);
      setActiveSubMenu(null);
      document.body.style.overflow = "visible";
    }
  };

  const handleMainMenuMobileClose = () => {
    setIsMainMenuMobileOpen(false);
    setActiveSubMenu(null);
    document.body.style.overflow = "visible";
  };

  const handleSubMenuOpen = (menuId: string | null) => {
    setActiveSubMenu(menuId);
  };

  const handleMainMenuMobileOpen = () => {
    setIsMainMenuMobileOpen(true);
    document.body.style.overflow = "hidden";
  };

  const handleReturn = () => {
    navigate(-1);
  }

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (headerType === "modal") {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/');
      }
    } else {
      navigate(-1);
    }
  }

  const mainMenu = useMemo(() => {
    return (
      <ul className={`${styles.mainMenu_list} ${activeSubMenu ? styles.subMenuActive : ""}`}>
        {
          menuItems.map(({ id, name, href, subMenu, icon }) => (
            <MainMenuItem
              key={id}
              id={id}
              name={name}
              href={href}
              subMenu={subMenu}
              icon={icon}
              handleMainMenuMobileClose={handleMainMenuMobileClose}
              activeSubMenu={activeSubMenu}
              onSubMenuOpen={handleSubMenuOpen}
            />
          ))
        }
      </ul>
    ) 
  }, [menuItems, activeSubMenu]);

  if (headerType === "modal") {
    return (
      <header className={`${styles.header} ${styles.headerModal}`}>
        <div className={styles.wrapperModal}>
          {returnButton && (
            <button className={styles.backButton} onClick={handleBack}>
              <img src={arrowLeftIcon} alt="back" />
            </button>
          )}
          <Title newStyles={styles.titleModal} mountLocation="header">
            {title}
          </Title>
        </div>
      </header>
    );
  }

  return (
    <header className={styles.header}>
      <div className={styles.wrapper + " container"}>
        <div className={styles.logo}>
          <Link to="/">
            <img src={logo} alt="Company's logo" />
          </Link>
        </div>

        <Title newStyles={styles.title} mountLocation="header">
          {title}
        </Title>

        <nav
          className={`${styles.mainMenu} ${isMainMenuMobileOpen ? styles.active : ""}`}
          ref={mainMenuRef}
        >
          {isMainMenuMobileOpen && (
            <button
              className={styles.closeButton}
              onClick={handleMainMenuMobileClose}
              aria-label="Закрыть меню"
              type="button"
            >
              <span></span>
              <span></span>
            </button>
          )}
          {mainMenu}
        </nav>

        <HeaderRightButton 
          onBurgerClick={handleMainMenuMobileOpen}
          isBurgerOpen={isMainMenuMobileOpen}
        />
      </div>
    </header>
  );
};
